// @ts-strict-ignore
/**
 * High-level document generator for Workflow decision criteria.
 *
 * Responsibilities:
 * - Build a stable, human-readable Markdown document for a Workflow.
 * - Keep orchestration concerns (ordering, sections) separate from
 *   threshold-specific rendering (delegated to threshold.ts) and
 *   formatting utilities (formatting.ts).
 *
 * Extension points:
 * - Add new sections by composing in this class only.
 * - Add new threshold rendering rules in threshold.ts without touching orchestration.
 */
import type {
  Action,
  BooleanRule,
  Dependency,
  IndexedRule,
  ReasonCodeGroup,
  Rules,
  SchemaInstance,
  Expression,
  ThresholdResults,
  ThresholdResult,
} from 'flow/store/engine/types';
import { isMatrix1Dimensional, getCommaSeparatedMatrixValues } from 'flow/service/actions/matrixModels';
import { describeMatrixAxis, getActionDescription } from './formatting';
import { describeThreshold as describeThresholdExternal } from './threshold';
import type { CustomList } from 'flow/service/actions/services/customLists/types';

/**
 * Threshold results map keyed by action ID (legacy or public)
 */
export type ThresholdResultsMap = Record<string, ThresholdResults>;

/**
 * Canvas layout ordering used to make the doc follow the visual editor flow.
 */
const TYPE_SORT_ORDER: Record<string, number> = {
  input: 0,
  service: 2,
  tag: 3,
  output: 4,
  matrix_model: 5,
  outcome: 99,
  outcome_reason: 99,
  exit: 99,
  preview: 99,
  workflow: 99,
  imported_matrix_model: 99,
  imported_output_attribute: 99,
};

/**
 * Action plus local dependency metadata used for ordering in the document.
 */
interface ActionWithDependencies extends Action {
  dependencies: Dependency[];
}

/**
 * Processed action with its final display metadata for section ordering.
 */
interface ProcessedAction {
  action: ActionWithDependencies;
  order: number;
  column: number;
  typeOrder: number;
  label: string;
}

/**
 * Legacy ID mappings from the workflow transformation.
 * Maps legacy action/threshold IDs (numbers as strings) to public IDs (uuids).
 */
export interface LegacyMappings {
  actions: Record<string, string>;
  thresholds: Record<string, string>;
}

export class WorkflowDocumentGenerator {
  private readonly workflow: Partial<SchemaInstance>;

  private readonly services?: Record<string, any>;

  private readonly processedActions: Map<string, ProcessedAction>;

  private readonly customLists?: CustomList[];

  private readonly thresholdResults?: ThresholdResultsMap;

  private readonly legacyMappings?: LegacyMappings;

  private readonly legacyActionIdMap: Map<string, string>;

  private readonly legacyThresholdIdMap: Map<string, string>;

  constructor(
    workflow: Partial<SchemaInstance>,
    services?: Record<string, any>,
    customLists?: CustomList[],
    thresholdResults?: ThresholdResultsMap,
    legacyMappings?: LegacyMappings,
  ) {
    this.workflow = workflow;
    this.services = services;
    this.processedActions = new Map();
    this.customLists = customLists;
    this.thresholdResults = thresholdResults;
    this.legacyMappings = legacyMappings;
    this.legacyActionIdMap = new Map();
    this.legacyThresholdIdMap = new Map();

    // Build legacy ID maps if available
    this.buildLegacyIdMaps();
  }

  /**
   * Build mappings from legacy IDs to public IDs for actions and thresholds.
   * Legacy mappings format: { legacyId: publicId }
   */
  private buildLegacyIdMaps(): void {
    if (this.legacyMappings?.actions) {
      Object.entries(this.legacyMappings.actions).forEach(([legacyId, publicId]) => {
        this.legacyActionIdMap.set(legacyId, publicId as string);
      });
    }
    if (this.legacyMappings?.thresholds) {
      Object.entries(this.legacyMappings.thresholds).forEach(([legacyId, publicId]) => {
        this.legacyThresholdIdMap.set(legacyId, publicId as string);
      });
    }
  }

  /**
   * Get the legacy action ID for a public action ID (reverse lookup)
   */
  private getLegacyActionId(publicId: string): string | null {
    for (const [legacyId, pubId] of this.legacyActionIdMap.entries()) {
      if (pubId === publicId) {
        return legacyId;
      }
    }
    return null;
  }

  /**
   * Get the legacy threshold ID for a public threshold ID (reverse lookup)
   */
  private getLegacyThresholdId(publicId: string): string | null {
    for (const [legacyId, pubId] of this.legacyThresholdIdMap.entries()) {
      if (pubId === publicId) {
        return legacyId;
      }
    }
    return null;
  }

  /**
   * Get threshold result for a specific threshold within an action.
   * Handles lookups by both legacy and public IDs since the API returns legacy IDs.
   */
  private getThresholdResult(actionPublicId: string, thresholdId: string): ThresholdResult | null {
    if (!this.thresholdResults) return null;

    // Get the legacy action ID since threshold results are keyed by legacy IDs
    const legacyActionId = this.getLegacyActionId(actionPublicId);
    if (!legacyActionId) return null;

    const actionResults = this.thresholdResults[legacyActionId];
    if (!actionResults?.thresholds) return null;

    // Get the legacy threshold ID since results use legacy IDs
    const legacyThresholdId = this.getLegacyThresholdId(thresholdId);
    if (!legacyThresholdId) return null;

    return actionResults.thresholds[legacyThresholdId] || null;
  }

  /**
   * Check if an action (tag/outcome) is "set" based on threshold results.
   * A tag/outcome is considered set if any of its threshold results indicate is_tag_set = true.
   */
  private isActionSet(actionPublicId: string): boolean {
    if (!this.thresholdResults) return false;

    const legacyActionId = this.getLegacyActionId(actionPublicId);
    if (!legacyActionId) return false;

    const actionResults = this.thresholdResults[legacyActionId];
    if (!actionResults?.thresholds) return false;

    // Check if any threshold in this action indicates the tag/outcome was set
    return Object.values(actionResults.thresholds).some(
      (result: ThresholdResult) => result.is_tag_set === true,
    );
  }

  /**
   * Collect all action IDs that are relevant when filtering by threshold results.
   * Includes: set actions + all their dependencies (recursively).
   */
  private getRelevantActionIds(): Set<string> {
    const relevantIds = new Set<string>();

    // Find all actions that are "set"
    const setActionIds: string[] = [];
    Object.keys(this.workflow.actions || {}).forEach(actionId => {
      const action = this.workflow.actions![actionId];
      if ((action.type === 'tag' || action.type === 'outcome') && this.isActionSet(actionId)) {
        setActionIds.push(actionId);
      }
    });

    // Recursively collect dependencies for set actions
    const collectDependencies = (actionId: string) => {
      if (relevantIds.has(actionId)) return;
      relevantIds.add(actionId);

      const dependencies = this.workflow.dependencies?.[actionId] || [];
      dependencies.forEach(dep => {
        collectDependencies(dep.id);
      });
    };

    setActionIds.forEach(actionId => collectDependencies(actionId));

    return relevantIds;
  }

  public generateDocument(): string {
    if (!this.workflow.actions || !Object.keys(this.workflow.actions).length) return '';

    const doc: string[] = [];
    doc.push(this.generateHeader());

    // When threshold results are available, add a traceable path summary first
    if (this.thresholdResults) {
      doc.push(this.generateTriggeredPathSection());
    }

    doc.push(this.generateWorkflowNodeSection());
    doc.push(this.generateReasonCodeGroupsSection());
    doc.push(this.generateOutcomesSection());

    // Note: Threshold results are now integrated inline with each rule,
    // not as a separate section

    return doc.filter(Boolean).join('\n\n');
  }

  /**
   * Generates a tree-like visualization of the triggered decision path.
   * Shows how the outcome was reached through the chain of triggered nodes.
   */
  private generateTriggeredPathSection(): string {
    const sections: string[] = ['## Decision Path Trace\n'];

    // Find triggered outcomes
    const triggeredOutcomes = Object.values(this.workflow.actions || {})
      .filter(action => action.type === 'outcome' && this.isActionSet(action.publicId));

    if (triggeredOutcomes.length === 0) {
      sections.push('No outcome was triggered.');
      return sections.join('\n');
    }

    // For each triggered outcome, trace back the path
    triggeredOutcomes.forEach(outcome => {
      sections.push(`OUTCOME: ${outcome.outcome?.name}`);
      sections.push(this.traceActionPath(outcome.publicId, 1, new Set()));
    });

    return sections.join('\n');
  }

  /**
   * Recursively traces the path of an action through its dependencies.
   * @param actionId The action to trace
   * @param depth Current indentation depth
   * @param visited Set of already visited action IDs to prevent cycles
   */
  private traceActionPath(actionId: string, depth: number, visited: Set<string>): string {
    if (visited.has(actionId)) return '';
    visited.add(actionId);

    const action = this.workflow.actions?.[actionId];
    if (!action) return '';

    const lines: string[] = [];
    const indent = '  '.repeat(depth);
    const prefix = depth === 1 ? '|-- ' : '|-- ';

    // Get dependencies for this action
    const dependencies = this.workflow.dependencies?.[actionId] || [];

    // Process dependencies
    dependencies.forEach(dep => {
      const depAction = this.workflow.actions?.[dep.id];
      if (!depAction) return;

      const depLabel = WorkflowDocumentGenerator.getActionPathLabel(depAction);
      const isSet = this.isActionSet(dep.id);

      // Determine status suffix for tag/outcome actions based on whether they're set
      const showStatus = depAction.type === 'tag' || depAction.type === 'outcome';
      const status = showStatus ? WorkflowDocumentGenerator.getSetStatusLabel(isSet) : '';

      lines.push(`${indent}${prefix}${depLabel}${status}`);

      // Recursively trace deeper dependencies
      const subPath = this.traceActionPath(dep.id, depth + 1, visited);
      if (subPath) {
        lines.push(subPath);
      }
    });

    return lines.join('\n');
  }

  /**
   * Gets a human-readable label for an action in the path trace.
   */
  private static getActionPathLabel(action: Action): string {
    switch (action.type) {
      case 'service':
        return `Service: ${action.service?.name || 'Unknown'}`;
      case 'tag':
        return `Tag: ${action.tag?.name || 'Unknown'}`;
      case 'input':
        return `Input: ${action.input?.name || 'Unknown'}`;
      case 'output':
        return `Output: ${action.output?.name || 'Unknown'}`;
      case 'outcome':
        return `Outcome: ${action.outcome?.name || 'Unknown'}`;
      case 'matrix_model':
        return `Matrix: ${action.matrixModel?.name || 'Unknown'}`;
      default:
        return `${action.type}: Unknown`;
    }
  }

  /**
   * Returns the status label for whether an action is set or not.
   */
  private static getSetStatusLabel(isSet: boolean): string {
    return isSet ? ' (SET)' : ' (NOT SET)';
  }

  private generateHeader(): string {
    if (this.thresholdResults) {
      return `# Evaluation Results for ${this.workflow.name} v${this.workflow.publicVersion}

This document shows the triggered decision paths and their evaluation results. Only nodes that were set (triggered) and their dependencies are included.

Note: Each rule includes Rule Explainability data in the format [Evaluation Result: ..., Calculated Value: ...]. This describes how each rule was executed during the evaluation - "Evaluation Result" indicates whether the rule condition was satisfied, and "Calculated Value" shows the actual value that was computed or retrieved during execution.`;
    }

    return `# Decision Criteria Documentation for ${this.workflow.name} v${this.workflow.publicVersion}

This document details every decision node, rule/threshold, and outcome configured in your Alloy workflow.`;
  }

  /**
   * Renders the main node criteria section in visual-editor order.
   * When threshold results are available, filters to only show relevant nodes.
   */
  private generateWorkflowNodeSection(): string {
    this.processActionDependencies();
    const sortedActions = this.getSortedActions();

    // When threshold results are available, filter to only relevant actions
    const relevantIds = this.thresholdResults ? this.getRelevantActionIds() : null;
    const filteredActions = relevantIds
      ? sortedActions.filter(({ action }) => relevantIds.has(action.publicId))
      : sortedActions;

    const headerNote = this.thresholdResults
      ? '## Evaluated Workflow Nodes (filtered to triggered paths)\n'
      : '## Workflow Node Criteria and Logic\n';

    const sections: string[] = [headerNote];
    for (const { action } of filteredActions) {
      sections.push(this.generateActionSection(action));
    }

    return sections.join('\n\n');
  }

  /**
   * Pre-computes ordering metadata for actions to mirror the editor's layout.
   */
  private processActionDependencies(): void {
    const maxPathLengthMap = this.getMaxPathLengthMap();

    Object.entries(this.workflow.actions).forEach(([actionId, action]) => {
      const dependencies = this.workflow.dependencies?.[actionId] || [];
      const column = maxPathLengthMap.get(actionId) || 0;
      const typeOrder = TYPE_SORT_ORDER[action.type] || 999;
      const label = WorkflowDocumentGenerator.getActionLabel(action);

      this.processedActions.set(actionId, {
        action: { ...action, dependencies },
        order: -1,
        column,
        typeOrder,
        label,
      });
    });

    const sortedActions = this.getSortedActions();
    sortedActions.forEach((processedAction, index) => {
      processedAction.order = index;
      this.processedActions.set(processedAction.action.publicId, processedAction);
    });
  }

  /**
   * Computes the longest dependency path length for each action.
   * Used as the column index for ordering.
   */
  private getMaxPathLengthMap(): Map<string, number> {
    const maxPathLengthMap = new Map<string, number>();

    const traverseDependencies = (actionId: string): number => {
      if (maxPathLengthMap.has(actionId)) {
        return maxPathLengthMap.get(actionId)!;
      }

      const dependencies = this.workflow.dependencies?.[actionId] || [];

      if (dependencies.length === 0) {
        maxPathLengthMap.set(actionId, 0);
        return 0;
      }

      let maxPathLength = -Infinity;
      for (const dependency of dependencies) {
        const result = traverseDependencies(dependency.id) + 1;
        maxPathLength = Math.max(maxPathLength, result);
      }

      maxPathLengthMap.set(actionId, maxPathLength);
      return maxPathLength;
    };

    Object.keys(this.workflow.actions).forEach(actionId => {
      traverseDependencies(actionId);
    });

    return maxPathLengthMap;
  }

  /**
   * Human label used for sub-sorting within a column/type.
   */
  private static getActionLabel(action: Action): string {
    switch (action.type) {
      case 'service':
        return action.service?.name || '';
      case 'tag':
        return action.tag?.name || '';
      case 'input':
        return `${action.input?.name || ''} ${action.input?.dataType || ''}`.trim() || '';
      case 'output':
        return action.output?.name || '';
      case 'outcome':
        return action.outcome?.name || '';
      case 'outcome_reason':
        return action.outcomeReason?.name || '';
      case 'matrix_model':
        return action.matrixModel?.name || '';
      case 'imported_matrix_model':
        return action.importedMatrixModel?.name || '';
      case 'imported_output_attribute':
        return action.importedOutputAttribute?.name || '';
      case 'workflow':
        return action.workflow?.name || '';
      default:
        return action.type;
    }
  }

  /**
   * Sorter that mirrors the editor ordering:
   * - Inputs first; otherwise by dependency depth, then type, then alphabetically.
   */
  private getSortedActions(): ProcessedAction[] {
    return Array.from(this.processedActions.values())
      .sort((a, b) => {
        const aIsInput = a.action.type === 'input';
        const bIsInput = b.action.type === 'input';

        if (aIsInput && !bIsInput) return -1;
        if (!aIsInput && bIsInput) return 1;

        if (a.column !== b.column) {
          return a.column - b.column;
        }

        if (a.typeOrder !== b.typeOrder) {
          return a.typeOrder - b.typeOrder;
        }

        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      });
  }

  /**
   * Chooses the appropriate section generator per action type.
   */
  private generateActionSection(action: ActionWithDependencies): string {
    const sections: string[] = [];

    switch (action.type) {
      case 'input':
        sections.push(WorkflowDocumentGenerator.generateInputSection(action));
        break;
      case 'service':
        sections.push(this.generateServiceSection(action));
        break;
      case 'tag':
        sections.push(this.generateTagSection(action));
        break;
      case 'output':
        sections.push(this.generateOutputSection(action));
        break;
      case 'matrix_model':
        sections.push(WorkflowDocumentGenerator.generateMatrixModelSection(action));
        break;
    }

    return sections.join('\n\n');
  }

  /**
   * Input section (simple header).
   */
  private static generateInputSection(action: ActionWithDependencies): string {
    if (!action.input) return '';
    return `### Input: ${action.input.name}`;
  }

  /**
   * Service section: name, optional modules, and run rules.
   */
  private generateServiceSection(action: ActionWithDependencies): string {
    if (!action.service) return '';

    const sections: string[] = [];
    sections.push(`### Run service: ${action.service.name}`);

    const modules = action.options?.modules?.value;
    if (modules && typeof modules === 'string') {
      sections.push(`Modules enabled: ${modules.split(',').join(', ')}`);
    }

    if (Object.keys(action.rules).length > 0) {
      sections.push('This service runs when the following criteria are met:');
      sections.push(this.generateRuleDescription(action.rules, action.publicId));
    } else {
      sections.push('This service always runs.');
    }

    return sections.join('\n');
  }

  /**
   * Tag section: name and triggering rules.
   * When threshold results are available, includes (Set) or (Not Set) status.
   */
  private generateTagSection(action: ActionWithDependencies): string {
    if (!action.tag) return '';

    const sections: string[] = [];

    // Add status indicator when in threshold results mode
    const statusSuffix = this.thresholdResults ? WorkflowDocumentGenerator.getSetStatusLabel(this.isActionSet(action.publicId)) : '';
    sections.push(`### Tag: ${action.tag.name}${statusSuffix}`);

    if (Object.keys(action.rules).length > 0) {
      sections.push('This tag is triggered when the following conditions are met:');
      sections.push(this.generateRuleDescription(action.rules, action.publicId));
    } else {
      sections.push('This tag is always set.');
    }

    return sections.join('\n');
  }

  /**
   * Output section: name and expression rendering.
   */
  private generateOutputSection(action: ActionWithDependencies): string {
    if (!action.output) return '';

    const sections: string[] = [];
    sections.push(`### Output: ${action.output.name}`);
    if (action.output.expression) {
      sections.push('Calculated using:');
      sections.push(this.generateExpressionDescription(action.output.expression));
    }

    return sections.join('\n');
  }

  /**
   * Matrix Model section with axis, data type, and output values summary.
   */
  private static generateMatrixModelSection(action: ActionWithDependencies): string {
    if (!action.matrixModel) return '';

    const { matrixModel } = action;
    const is1D = isMatrix1Dimensional(matrixModel.axis.x);
    const dimensionLabel = is1D ? '(1D)' : '(2D)';

    const sections: string[] = [];
    sections.push(`### Matrix Model ${dimensionLabel}: ${matrixModel.name}`);
    sections.push('Matrix configuration:');

    if (!is1D) {
      sections.push(`- X axis: ${describeMatrixAxis(matrixModel.axis.x)}`);
    }
    sections.push(`- Y axis: ${describeMatrixAxis(matrixModel.axis.y)}`);
    sections.push(`- Data type: ${matrixModel.matrixDataType}`);

    const outputValues = getCommaSeparatedMatrixValues(matrixModel.matrix);
    if (outputValues) {
      sections.push(`- Output values: ${outputValues}`);
    }

    return sections.join('\n');
  }

  /**
   * Pretty-prints nested boolean rules by delegating leaf IDs to threshold renderer.
   * @param rules The rules to render
   * @param actionPublicId The public ID of the action these rules belong to (for threshold result lookup)
   * @param indent Current indentation level
   */
  private generateRuleDescription(
    rules: Rules,
    actionPublicId: string,
    indent = 0,
  ): string {
    if (typeof rules === 'string') {
      return this.describeThreshold(rules, actionPublicId);
    }

    const ruleObj = rules as BooleanRule | IndexedRule;
    if ('id' in ruleObj) {
      return this.describeThreshold(ruleObj.id, actionPublicId);
    }

    const indentStr = '    '.repeat(indent);
    const nextIndentStr = '    '.repeat(indent + 1);

    if ('and' in ruleObj && ruleObj.and) {
      const conditions = ruleObj.and.map(r => {
        const conditionDesc = this.generateRuleDescription(r, actionPublicId, indent + 1);
        return `${nextIndentStr}${conditionDesc}`;
      });
      return `(\n${conditions.join(' AND\n')}\n${indentStr})`;
    }

    if ('or' in ruleObj && ruleObj.or) {
      const conditions = ruleObj.or.map(r => {
        const conditionDesc = this.generateRuleDescription(r, actionPublicId, indent + 1);
        return `${nextIndentStr}${conditionDesc}`;
      });
      return `(\n${conditions.join(' OR\n')}\n${indentStr})`;
    }

    return '';
  }

  /**
   * Delegates threshold rendering to the rules-based renderer.
   * Includes evaluation results inline if threshold results are available.
   */
  private describeThreshold(thresholdId: string, actionPublicId: string): string {
    const baseDescription = describeThresholdExternal({
      services: this.services,
      workflow: this.workflow,
      thresholdId,
      getReasonCodeGroupName: (id: number) => this.getReasonCodeGroupName(id),
      customLists: this.customLists,
    });

    // If we have threshold results, append the evaluation result inline
    if (this.thresholdResults) {
      const result = this.getThresholdResult(actionPublicId, thresholdId);
      if (result) {
        // Determine the calculated value to display (matches RuleLineItemOutputValue.tsx logic)
        let calculatedValue: string;
        if (result.is_tag_set !== null) {
          calculatedValue = result.is_tag_set ? 'Set' : 'Not Set';
        } else {
          calculatedValue = String(result.left_value ?? 'No value');
        }

        // Build the result annotation with clear labeling
        const evaluationResult = result.is_true ? 'Satisfied' : 'Not Satisfied';

        return `${baseDescription} [Evaluation Result: ${evaluationResult}, Calculated Value: ${calculatedValue}]`;
      }
    }

    return baseDescription;
  }

  /**
   * Collates and renders a summary of all reason code groups referenced by thresholds.
   */
  private generateReasonCodeGroupsSection(): string {
    const reasonCodeGroups = new Set<number>();

    Object.values(this.workflow.thresholds || {}).forEach(threshold => {
      if (threshold.right?.reasonCodeGroupId) {
        reasonCodeGroups.add(threshold.right.reasonCodeGroupId);
      }
    });

    if (reasonCodeGroups.size === 0) return '';

    const sections = ['## Reason Code Groups Used in Criteria\n'];

    reasonCodeGroups.forEach(groupId => {
      const group = this.getReasonCodeGroupById(groupId);
      if (group) {
        sections.push(`**${group.name}:**`);
        group.reasonCodes.forEach(code => {
          if (code) {
            if (typeof code === 'object') {
              sections.push(`- ${code.code}: ${code.description} (from ${code.service_name})`);
            }
          }
        });
        sections.push('');
      }
    });

    return sections.join('\n');
  }

  /**
   * Renders outcomes in precedence order with their triggering rules.
   * When threshold results are available, only shows triggered outcomes.
   */
  private generateOutcomesSection(): string {
    const relevantIds = this.thresholdResults ? this.getRelevantActionIds() : null;

    const sortedOutcomes = Array.from(this.processedActions.values())
      .filter(processedAction => {
        if (processedAction.action.type !== 'outcome') return false;
        // When filtering, only include set outcomes
        if (relevantIds && !relevantIds.has(processedAction.action.publicId)) return false;
        return true;
      })
      .sort((a, b) => {
        const aPrecedence = a.action.outcome?.precedenceIndex ?? 0;
        const bPrecedence = b.action.outcome?.precedenceIndex ?? 0;
        return bPrecedence - aPrecedence;
      });

    if (sortedOutcomes.length === 0) return '';

    const headerNote = this.thresholdResults
      ? '## Triggered Outcome\n'
      : '## Outcome Criteria\n';

    const sections = [headerNote];

    sortedOutcomes.forEach(({ action: outcome }) => {
      if (!outcome.outcome) return;

      // Add status indicator when in threshold results mode
      let statusSuffix = '';
      if (this.thresholdResults) {
        statusSuffix = this.isActionSet(outcome.publicId) ? ' (Triggered)' : ' (Not Triggered)';
      }
      sections.push(`### Outcome: ${outcome.outcome.name}${statusSuffix}`);

      if (Object.keys(outcome.rules).length > 0) {
        sections.push(this.generateRuleDescription(outcome.rules, outcome.publicId));
      } else {
        sections.push('This outcome is a default outcome. It is set when no other outcome is set.');
      }
      sections.push('');
    });

    return sections.join('\n');
  }

  /**
   * Finds a reason code group by scanning thresholds (source of truth in editor state).
   */
  private getReasonCodeGroupById(groupId: number): ReasonCodeGroup | null {
    for (const threshold of Object.values(this.workflow.thresholds || {})) {
      if (threshold.right?.reasonCodeGroupId === groupId && threshold.right?.reasonCodeGroup) {
        return threshold.right.reasonCodeGroup;
      }
    }
    return null;
  }

  /**
   * Returns a stable name for a reason code group; falls back to an ID label.
   */
  private getReasonCodeGroupName(groupId: number): string {
    const group = this.getReasonCodeGroupById(groupId);
    return group ? group.name : `Group ${groupId}`;
  }

  /**
   * Renders an expression by mapping data-route terms to their source action labels.
   */
  private generateExpressionDescription(expression: Expression): string {
    return expression.map(term => {
      if (typeof term === 'object' && 'data_route_id' in term) {
        const route = this.workflow.dataRoutes[term.data_route_id];
        if (route) {
          const sourceAction = this.workflow.actions[route.sourceId];
          return getActionDescription(sourceAction);
        }
        return '';
      }
      return term.toString();
    }).join(' ');
  }
}

/**
 * Public entrypoint used by UI to generate the full Markdown document.
 */
export function generateWorkflowDocumentation(
  workflow: Partial<SchemaInstance>,
  services?: Record<string, any>,
  customLists?: CustomList[],
): string {
  const generator = new WorkflowDocumentGenerator(workflow, services, customLists);
  return generator.generateDocument();
}

/**
 * Generate workflow documentation with threshold evaluation results merged in.
 * Used for providing rich context to LLM summaries.
 *
 * @param workflow - Transformed workflow data (SchemaInstance from Redux)
 * @param thresholdResults - Threshold evaluation results keyed by legacy action ID
 * @param services - Services configuration from Redux
 * @param customLists - Custom lists from Redux
 * @param legacyMappings - Legacy ID mappings from Redux for action/threshold ID translation
 */
export function generateWorkflowDocumentationWithResults(
  workflow: Partial<SchemaInstance>,
  thresholdResults?: ThresholdResultsMap,
  services?: Record<string, any>,
  customLists?: CustomList[],
  legacyMappings?: LegacyMappings,
): string {
  const generator = new WorkflowDocumentGenerator(
    workflow,
    services,
    customLists,
    thresholdResults,
    legacyMappings,
  );
  return generator.generateDocument();
}

